import { ClientBusinessLine } from '../enums/ClientBusinessLine';
import { ClientType } from '../enums/ClientType';
import { ClientBillingPackage } from './Billing';
import { mapToClientBusinessLine } from './ClientBusinessLine';
import { InboxFilter } from './InboxTypes';
import { UserRoles } from './UserRoles';

export interface UserConfig {
    //client scoped
    isActive: boolean;
    clientId: number;
    businessLine: ClientBusinessLine;
    timeZoneId: string;
    clientFirstName: string;
    clientLastName: string;
    clientHasUsersWithDialer: boolean;
    clientHasSic: boolean; //if the sic platform is active for this client
    clientType: ClientType;
    clientHasWebPush: boolean;
    clientHasEngagePlatform: boolean; //if client has engage platform
    //user scoped
    userName: string;
    isImpersonated: boolean;
    impersonatedUsername: string | undefined;
    userRoles: UserRoles[];
    userEmail: string | undefined;
    userPhone: string | undefined;
    userFirstName: string | undefined;
    userLastName: string | undefined;
    userColour: string | undefined;
    hasPushNotificationsEnabled: boolean;
    engageToken: string | undefined; //for quick login to engage
    //dialer feature
    dialerIsEnabled: boolean; //if they have the dialer feature
    dialerIsSetup: boolean; //if a phone number is setup for the dialer
    dialerIsSic: boolean; //if this user has a phone number pointed to the sic platform
    dialerPhoneId: number;
    dialerPhone: string | undefined;
    //google feature
    googleAccountId: string | undefined;
    googleSessionExpired: boolean;
    googleCalendarId: string | undefined;
    googleCalendarChannelId: string | undefined;
    //user email feature
    userEmailId: string | undefined; //specific to the "user email" feature
    userEmailBroken: boolean;
    //ai
    chatReplyPromptId: number;
    transcriptPromptId: number;
    transcriptSummaryPromptId: number;
    //additional misc data
    users: UserInfo[] | undefined; //list of users for this client, used in various places across the app
    inboxFilter: InboxFilter | undefined;
    billingPackage: ClientBillingPackage | undefined;
    hasBusinessProfile: boolean;
    selectedBusinessProfile: number | undefined;
    //referralsFeature
    hasReferralsPaid: boolean;
    activePackage: ActivePackage | undefined;
    activePackageFlags: ActivePackageFlags | undefined;
    defaultListId: number | undefined;
    defaultListCode: string | undefined;
}

export interface UserConfigResponse {
    isActive: boolean;
    clientid: number;
    businessLine: string;
    clientfirstname: string;
    clientlastname: string;
    clienthasengage: boolean;
    users: UserInfo[];
    timeZoneId: string;
    type: ClientType;
    roles: UserRoles[];
    email: string;
    username: string;
    firstname: string;
    lastname: string;
    phone: string;
    colour: string;
    hasPush: boolean;
    hasEngage: boolean;
    hasSiC: boolean;
    hasWebPush: boolean;
    engagePhone: string;
    engagePhoneId: number;
    googleAccountId: string;
    googleSessionExpired: boolean;
    userEmailId: string;
    userEmailBroken: boolean;
    googleCalendarId: string;
    googleCalendarChannelId: string;
    inboxFilter: InboxFilter | undefined;
    engageToken: string;
    isImpersonated: boolean;
    impusername: string | undefined;
    chatReplyPromptId: number;
    transcriptPromptId: number;
    transcriptSummaryPromptId: number;
    clientHasSic: boolean;
    clientHasEngagePlatform: boolean;
    billingPackage: ClientBillingPackage;
    hasBusinessProfile: boolean;
    selectedBusinessProfile: number | undefined;
    hasReferralsPaid: boolean;
    activePackage: ActivePackage | undefined;
    activePackageFlags: ActivePackageFlags | undefined;
    defaultListId: number | undefined;
    defaultListCode: string | undefined;
}

export interface UserInfo {
    clientId: number;
    firstName: string;
    lastName: string;
    isActive: boolean;
    hasDialer: boolean;
    dialerIsSic: boolean;
    email: string;
    phone: string;
    userName: string;
    userEmailId: string;
    userEmailBroken: boolean;
    googleAccountId: string;
    hasPush: boolean;
    twilioPhoneId: number;
    twilioPhoneNumber: string;
    twilioAppSiD: string;
    calendarId: string;
    calendarChannelId: string;
    colour: string;
}

export function mapUserConfigResponseToUserConfig(response: UserConfigResponse): UserConfig {
    return {
        isActive: response.isActive,
        clientId: response.clientid,
        businessLine: mapToClientBusinessLine(response.businessLine),
        clientFirstName: response.clientfirstname,
        clientLastName: response.clientlastname,
        clientHasUsersWithDialer: response.clienthasengage,
        clientHasSic: response.clientHasSic,
        clientHasWebPush: response.hasWebPush,
        clientHasEngagePlatform: response.clientHasEngagePlatform,
        timeZoneId: response.timeZoneId,
        clientType: response.type,
        users: response.users,
        userRoles: response.roles,
        userColour: response.colour,
        userEmail: response.email,
        userEmailBroken: response.userEmailBroken,
        userPhone: response.phone,
        userName: response.username,
        userFirstName: response.firstname,
        userLastName: response.lastname,
        dialerIsEnabled: response.hasEngage,
        dialerPhone: response.engagePhone,
        dialerPhoneId: response.engagePhoneId,
        dialerIsSetup: !!response.engagePhone,
        dialerIsSic: response.hasSiC,
        googleAccountId: response.googleAccountId,
        googleSessionExpired: response.googleSessionExpired,
        googleCalendarId: response.googleCalendarId,
        googleCalendarChannelId: response.googleCalendarChannelId,
        userEmailId: response.userEmailId,
        hasPushNotificationsEnabled: response.hasPush,
        inboxFilter: response.inboxFilter,
        engageToken: response.engageToken,
        isImpersonated: response.isImpersonated,
        impersonatedUsername: response.impusername,
        chatReplyPromptId: response.chatReplyPromptId,
        transcriptPromptId: response.transcriptPromptId,
        transcriptSummaryPromptId: response.transcriptSummaryPromptId,
        billingPackage: response.billingPackage,
        hasBusinessProfile: response.hasBusinessProfile,
        selectedBusinessProfile: response.selectedBusinessProfile ?? undefined,
        hasReferralsPaid: response.hasReferralsPaid,
        activePackage: response.activePackage ?? undefined,
        activePackageFlags: response.activePackageFlags ?? undefined,
        defaultListId: response.defaultListId,
        defaultListCode: response.defaultListCode ?? undefined,
    };
}

export interface ActivePackage {
    packageId: number;
    name: string | undefined;
    key: string | undefined;
    activeDate: string;
    expiryDate: string;
}

export interface PackageFlags {
    name: string | undefined;
    isActive: boolean;
    hasLimit: boolean;
    limit: number;
}

export interface ActivePackageFlags {
    offers: PackageFlags;
    trustedPartnersSendInvites: PackageFlags;
    trustedPartnersInNetwork: PackageFlags;
    profileAppearanceCardLayout: PackageFlags;
    multipleNetworks: PackageFlags;
    networkSetCustomOffers: PackageFlags;
    networkHighlightPartners: PackageFlags;
    networkPartnerComments: PackageFlags;
    networkCustomizationLogo: PackageFlags;
    networkCustomizationBanner: PackageFlags;
    networkCustomizationDescription: PackageFlags;
    networkCustomizationVisibility: PackageFlags;
    networkCustomizationHost: PackageFlags;
    networkCustomizationConnectedNetworks: PackageFlags;
    networkCustomizationQualityIntroductions: PackageFlags;
    multiUser: PackageFlags;
    webinars: PackageFlags;
    webinarTopics: PackageFlags;
}
